import React, { useState } from "react";
import "./App.css";
import axios from "axios";
import WelcomeScreen from "./components/WelcomeScreen";
import QuestionScreen from "./components/QuestionScreen";
import AnalysisScreen from "./components/AnalysisScreen";
import ResultScreen from "./components/ResultScreen";
import ContactForm from "./components/ContactForm";
import ConfirmationScreen from "./components/ConfirmationScreen";
import { QUESTIONS } from "./data/questions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP_WELCOME = 0;
const STEP_ANALYSIS = 6;
const STEP_RESULTS = 7;
const STEP_FORM = 8;
const STEP_CONFIRMATION = 9;

function App() {
  const [step, setStep] = useState(STEP_WELCOME);
  const [answers, setAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [gainMinutes, setGainMinutes] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const runningGain = answers.reduce((s, a) => s + a.gain_minutes, 0);

  const handleStart = () => setStep(1);

  const handleAnswer = (questionIndex, answer) => {
    const newAnswers = [...answers.slice(0, questionIndex), answer];
    setAnswers(newAnswers);
    if (questionIndex === 4) {
      const totalScore = newAnswers.reduce((s, a) => s + a.points, 0);
      const totalGain = newAnswers.reduce((s, a) => s + a.gain_minutes, 0);
      setScore(totalScore);
      setGainMinutes(totalGain);
      setStep(STEP_ANALYSIS);
    } else {
      setStep(questionIndex + 2);
    }
  };

  const handleAnalysisDone = () => setStep(STEP_RESULTS);
  const handleShowForm = () => setStep(STEP_FORM);

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        ...formData,
        answers: answers.map((a, i) => ({
          question_id: i + 1,
          label: a.label,
          points: a.points,
          gain_minutes: a.gain_minutes,
        })),
        score,
        gain_minutes: gainMinutes,
        rgpd_consent: formData.rgpd_consent,
      };
      const response = await axios.post(`${API}/submit`, payload);
      setResult(response.data);
      setStep(STEP_CONFIRMATION);
    } catch (err) {
      setSubmitError(
        err.response?.data?.detail ||
          "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    if (step === STEP_WELCOME) {
      return <WelcomeScreen onStart={handleStart} />;
    }
    if (step >= 1 && step <= 5) {
      const questionIndex = step - 1;
      return (
        <QuestionScreen
          question={QUESTIONS[questionIndex]}
          questionIndex={questionIndex}
          totalQuestions={5}
          onAnswer={(answer) => handleAnswer(questionIndex, answer)}
          runningGain={runningGain}
        />
      );
    }
    if (step === STEP_ANALYSIS) {
      return <AnalysisScreen onDone={handleAnalysisDone} />;
    }
    if (step === STEP_RESULTS) {
      return (
        <ResultScreen
          score={score}
          gainMinutes={gainMinutes}
          onContinue={handleShowForm}
        />
      );
    }
    if (step === STEP_FORM) {
      return (
        <ContactForm
          score={score}
          gainMinutes={gainMinutes}
          onSubmit={handleSubmit}
          submitting={submitting}
          error={submitError}
        />
      );
    }
    if (step === STEP_CONFIRMATION) {
      return <ConfirmationScreen result={result} />;
    }
    return <WelcomeScreen onStart={handleStart} />;
  };

  return (
    <div className="App">
      {renderStep()}
      <footer className="app-footer">Made by Groupe LBS with AI - v1.0.2</footer>
    </div>
  );
}

export default App;
